import * as React from 'react';

import { SUPPORT_EMAIL } from '@constants/emails';

import { Alert, CheckboxesGroup, Select, Text, Toggle } from '@components/common';
import {
  AUTH_METHOD_DISABLED_REASON,
  AUTH_METHOD_NAMES,
  ROLE_OPTIONS
} from '@components/GovernanceApp/components/constants';
import {
  AuthMethodRemovalModal,
  CalendarEventPrivacySelect,
  DefaultPermissionConditionSelect,
  DefaultRoleSelect,
  DefaultShareAccessSelect,
  PermissionsMenu
} from '@components/GovernanceApp/components/Permissions/components';
import { useGetProviderQuery } from '@components/GovernanceApp/components/Permissions/components';
import { SamlProvider } from '@components/GovernanceApp/components/Permissions/components';
import { RestrictedAction } from '@components/shared/RestrictedAction';
import { InfoCircleIcon } from '@components/svgs';
import { Tooltip } from '@components/Tooltip';
import { compact } from '@components/utils';
import { useAccount } from '@hooks/useAccount';
import { useFeature } from '@hooks/useFeature';
import { usePlan } from '@hooks/usePlan';
import { useUpdateAccountSettingMutation } from 'components/stores/whoami';
import { useCurrentAuthMethod } from 'hooks/useCurrentAuthMethod';

import { BetterDisabledFeatures } from '../BetterDisabledFeatures';
import { DisabledFeatures } from '../DisabledFeatures';

import { AuthMethodAdditionModal } from './components/AuthMethodAdditionModal';

export const Permissions: React.FC<React.PropsWithChildren<unknown>> = () => {
  const {
    account: {
      domain,
      permissions,
      auth_methods,
      openid_tenant_id,
      custom_openid,
      interview_room_access,
      calendar_events_visibility,
      highlight_reel_access,
      clip_access,
      insight_access,
      visibility_permission_condition
    },
    update
  } = useAccount();

  const authMethod = useCurrentAuthMethod();
  const [updateAccountSetting, { isSuccess, isError }] = useUpdateAccountSettingMutation();
  const hasTeamAccessControl = useFeature('team_access_control');
  const hasBetterFeatureRestrictions = useFeature('better_feature_restrictions');

  const handleToggleAccess = async (v: boolean) => {
    const newVal = v ? 'observer' : null;
    await update({ permissions: { ...permissions, join_by_domain: newVal as any } });
  };

  const handleToggleCalendarEventsVisibility = async (v: string) => {
    await updateAccountSetting({ key: 'calendar_events_visibility', value: v });
  };

  const handleToggleFundStudy = async (v: boolean) => {
    const newVal = v ? 'admin' : 'creator';
    await update({ permissions: { ...permissions, fund_study: newVal } });
  };

  const handleToggleApproveStudies = async (v: boolean) => {
    const newVal = v ? 'admin' : 'creator';
    await update({ permissions: { ...permissions, approve_studies: newVal } });
  };

  const handleSetPermission = async (permission: string, role: AccountRole) => {
    await update({ permissions: { ...permissions, [permission]: role } });
  };

  const handleSetPermissionCondition = async (v: PermissionCondition) => {
    await update({ visibility_permission_condition: v });
  };

  const handleToggleInterviewRoomAccess = async (v: ShareAccess) => {
    await update({ interview_room_access: v });
  };

  const handleToggleHighlightReelAccess = async (v: ShareAccess) => {
    await update({ highlight_reel_access: v });
  };

  const handleToggleClipAccess = async (v: ShareAccess) => {
    await update({ clip_access: v });
  };

  const handleToggleInsightAccess = async (v: ShareAccess) => {
    await update({ insight_access: v });
  };

  const [isConfirmingAuthMethodRemoval, setIsConfirmingAuthMethodRemoval] = React.useState(false);
  const [isConfirmingAuthMethodAddition, setIsConfirmingAuthMethodAddition] = React.useState(false);
  const [authMethodToChange, setAuthMethodToChange] = React.useState<string>('');
  const [pendingAuthMethods, setPendingAuthMethods] = React.useState<AuthMethod[]>([]);

  const handleToggleAuthMethods = async (v: AuthMethod[]) => {
    const originalAuthMethods = auth_methods;
    const isRemovingAuthMethod = originalAuthMethods.length > v.length;
    const isAddingAuthMethod = originalAuthMethods.length < v.length;

    if (isRemovingAuthMethod || isAddingAuthMethod) {
      const [changedMethod, setConfirming] = isRemovingAuthMethod
        ? [originalAuthMethods.find((method) => !v.includes(method)), setIsConfirmingAuthMethodRemoval]
        : [v.find((method) => !originalAuthMethods.includes(method)), setIsConfirmingAuthMethodAddition];

      if (changedMethod) {
        setAuthMethodToChange(changedMethod);
        setPendingAuthMethods(v);
        setConfirming(true);
        return;
      }
    } else {
      await update({ auth_methods: v });
    }
  };

  const handleConfirmAuthMethodChange = async () => {
    await update({ auth_methods: pendingAuthMethods });
    handleCancelAuthMethodChange();
  };

  const handleCancelAuthMethodChange = () => {
    setIsConfirmingAuthMethodRemoval(false);
    setIsConfirmingAuthMethodAddition(false);
    setAuthMethodToChange('');
    setPendingAuthMethods([]);
  };

  const { hasFeature } = usePlan();
  const { data: samlProvider } = useGetProviderQuery();

  return (
    <>
      <AuthMethodRemovalModal
        open={isConfirmingAuthMethodRemoval}
        onClose={handleCancelAuthMethodChange}
        onConfirm={handleConfirmAuthMethodChange}
        authMethodName={AUTH_METHOD_NAMES[authMethodToChange]}
      />
      <AuthMethodAdditionModal
        open={isConfirmingAuthMethodAddition}
        onClose={handleCancelAuthMethodChange}
        onConfirm={handleConfirmAuthMethodChange}
        authMethodName={AUTH_METHOD_NAMES[authMethodToChange]}
      />
      <Text as='h1' h='700' className='mb-1'>
        Permissions
      </Text>
      <Text h='500' color='gray-500' className='monitor:mb-6 mb-4'>
        Manage who can do what, authentication methods, and more.
      </Text>
      <div className='flex-1 rounded-md border border-gray-200 bg-white p-6'>
        <div className='mb-10'>
          <Text h='600' bold className='mb-1'>
            Workspace access
          </Text>
          <Text h='400' color='gray-500'>
            Enabling access allows users with an email address with this domain to join your workspace as observers
            without a direct invitation.
          </Text>
          {domain ? (
            <div className='mt-4 flex flex-row items-center space-x-2'>
              <Toggle on={!!permissions.join_by_domain} onToggle={handleToggleAccess} />
              <Text h='400'>
                Enable access for <b>{domain}.</b>
              </Text>
            </div>
          ) : (
            <Alert type='warning' className='mt-4'>
              You do not have a domain set on your account. Please contact {SUPPORT_EMAIL} to enable this feature.
            </Alert>
          )}
        </div>
        <div className='mb-5'>
          <Text h='600' bold className='mb-1'>
            Visibility of data
          </Text>
          <Text h='400' color='gray-500'>
            Control what account members can access.
          </Text>
        </div>

        {hasTeamAccessControl && visibility_permission_condition && (
          <div className='mb-5'>
            <Text h='500' bold className='mb-1'>
              Candidates, studies, and repository artifacts
            </Text>
            <Text h='400' color='gray-500'>
              Controls who can view candidates, studies and repository artifacts across your workspace. Note: individual
              artifacts can still be shared publicly by Admins and Creators.
            </Text>
            <div className='mt-2 flex flex-row items-center space-x-2'>
              <DefaultPermissionConditionSelect
                dataTestId='visibility_permission_condition_select'
                value={visibility_permission_condition}
                onChange={handleSetPermissionCondition}
              />
            </div>
          </div>
        )}

        <div className='mb-5'>
          <Text h='500' bold className='mb-1'>
            Candidate PII
          </Text>
          <Text h='400' color='gray-500'>
            Controls which roles can see candidate's Personally Identifiable Information (PII).
          </Text>
          <div className='mt-2 flex flex-row items-center space-x-2'>
            <DefaultRoleSelect
              dataTestId='view_pii_select'
              value={permissions.view_pii}
              onChange={(r) => handleSetPermission('view_pii', r)}
            />
          </div>
        </div>

        <div className='mb-10'>
          <Text h='500' bold className='mb-1'>
            Calendar event visibility
          </Text>
          <div className='flex items-center space-x-2'>
            <Text h='400' color='gray-500'>
              Controls how event details and participant information are shown to teammates across all calendar events
              in the workspace.
            </Text>

            <Tooltip
              className='cursor-pointer'
              content={
                'Admins can manage whether event titles, descriptions, attendees, research participant PII, and other details are visible to teammates across all calendar events created in the workspace, when viewed in external calendars (e.g., Google or Outlook).'
              }
              placement='right'
            >
              <InfoCircleIcon className='h-3 w-3' />
            </Tooltip>
          </div>
          <div className='mt-2 flex flex-row items-center space-x-2'>
            <CalendarEventPrivacySelect
              value={calendar_events_visibility || 'default'}
              onChange={handleToggleCalendarEventsVisibility}
            />
          </div>
        </div>

        <div className='mb-10'>
          <Text h='600' bold className='mb-1'>
            Default sharing settings of repository artifacts
          </Text>
          <Text h='400' color='gray-500 mb-5'>
            Controls the default sharing setting for each type of repository artifact. Admins and Creators can override
            these settings on a per-artifact basis.
          </Text>
          <div className='mb-4'>
            <Text h='500' bold className='mb-1'>
              Default Interview sharing
            </Text>
            <Text h='400' color='gray-500'>
              For every new interview room that gets created, including observer live streams.
            </Text>
            <div className='mt-2 flex flex-row items-center space-x-2'>
              <DefaultShareAccessSelect
                dataTestId='interview_room_access_select'
                value={interview_room_access}
                onChange={handleToggleInterviewRoomAccess}
              />
            </div>
          </div>
          <div className='mb-4'>
            <Text h='500' bold className='mb-1'>
              Default Highlight sharing
            </Text>
            <Text h='400' color='gray-500'>
              For every new highlight that gets created.
            </Text>
            <div className='mt-2 flex flex-row items-center space-x-2'>
              <DefaultShareAccessSelect
                dataTestId='clip_access_select'
                value={clip_access}
                onChange={handleToggleClipAccess}
              />
            </div>
          </div>
          <div className='mb-4'>
            <Text h='500' bold className='mb-1'>
              Default Reel sharing
            </Text>
            <Text h='400' color='gray-500'>
              For every new reel that gets created.
            </Text>
            <div className='mt-2 flex flex-row items-center space-x-2'>
              <DefaultShareAccessSelect
                dataTestId='highlight_reel_access_select'
                value={highlight_reel_access}
                onChange={handleToggleHighlightReelAccess}
              />
            </div>
          </div>
          <div className='mb-4'>
            <Text h='500' bold className='mb-1'>
              Default Insight sharing
            </Text>
            <Text h='400' color='gray-500'>
              For every new insight that gets created.
            </Text>
            <div className='mt-2 flex flex-row items-center space-x-2'>
              <DefaultShareAccessSelect
                dataTestId='insight_access_select'
                value={insight_access}
                onChange={handleToggleInsightAccess}
              />
            </div>
          </div>
        </div>
        <div className='mb-10'>
          <Text h='600' bold className='mb-1'>
            Creating custom attributes
          </Text>
          <Text h='400' color='gray-500'>
            Set which roles can update and create new attribute definitions.
          </Text>
          <Select<AccountRole>
            dataTestId='custom_attributes_select'
            className='max-w-64 mt-4 w-full'
            options={ROLE_OPTIONS}
            value={permissions.manage_attrs}
            onChange={(r) => handleSetPermission('manage_attrs', r)}
          />
        </div>
        <div className='mb-10'>
          <Text h='600' bold mb='4'>
            Authentication methods
          </Text>
          <Text h='400' color='gray-500' mb='4'>
            Specify which authentication methods can be used for accessing your workspace.
          </Text>
          <RestrictedAction feature='set_auth_methods'>
            {({ can, may }) => (
              <>
                <CheckboxesGroup
                  options={compact([
                    {
                      label: AUTH_METHOD_NAMES.password,
                      value: 'password',
                      helper: 'Allow email and password for access to your workspace.',
                      disabled: authMethod === 'password',
                      disabledReason: AUTH_METHOD_DISABLED_REASON,
                      pill: authMethod === 'password' ? 'YOUR CURRENT METHOD' : undefined,
                      className: 'opacity-100'
                    },
                    {
                      label: AUTH_METHOD_NAMES.google,
                      value: 'google',
                      helper: 'Members can sign in with a connected Google account.',
                      disabled: authMethod === 'google',
                      disabledReason: AUTH_METHOD_DISABLED_REASON,
                      pill: authMethod === 'google' ? 'YOUR CURRENT METHOD' : undefined,
                      className: 'opacity-100'
                    },
                    {
                      label: AUTH_METHOD_NAMES.saml,
                      value: 'saml',
                      helper: 'Integrate your workspace with SAML/OKTA.',
                      disabled: !samlProvider || authMethod === 'saml',
                      disabledReason: !samlProvider
                        ? 'SAML integration is only available on Enterprise accounts'
                        : AUTH_METHOD_DISABLED_REASON,
                      pill: authMethod === 'saml' ? 'YOUR CURRENT METHOD' : undefined,
                      className: 'opacity-100'
                    },
                    openid_tenant_id &&
                      !custom_openid && {
                        label: AUTH_METHOD_NAMES.openid_connect,
                        value: 'openid_connect',
                        helper: 'Integrate your workspace with OpenID.',
                        disabled: authMethod === 'openid_connect',
                        disabledReason: AUTH_METHOD_DISABLED_REASON,
                        pill: authMethod === 'openid_connect' ? 'YOUR CURRENT METHOD' : undefined,
                        className: 'opacity-100'
                      },
                    custom_openid && {
                      label: AUTH_METHOD_NAMES.openid_connect_intuit,
                      value: 'openid_connect_intuit',
                      helper: 'Integrate your workspace with OpenID.',
                      disabled: authMethod === 'openid_connect_intuit',
                      disabledReason: AUTH_METHOD_DISABLED_REASON,
                      pill: authMethod === 'openid_connect_intuit' ? 'YOUR CURRENT METHOD' : undefined,
                      className: 'opacity-100'
                    }
                  ])}
                  selected={auth_methods}
                  disabled={!can || !may}
                  onChange={(v) => handleToggleAuthMethods(v as AuthMethod[])}
                />
                {!hasFeature('saml') && (
                  <Alert type='promo' className='mt-4'>
                    <span>
                      SAML integration is only available on Enterprise accounts.
                      <a href='/plans' className='ml-1 font-bold'>
                        View plans
                      </a>
                    </span>
                  </Alert>
                )}
              </>
            )}
          </RestrictedAction>
        </div>
        {hasFeature('saml') && (
          <div className='mb-10'>
            <Text h='600' bold mb='4'>
              SAML configuration
            </Text>
            <SamlProvider />
          </div>
        )}
        <div className='mb-10'>
          <Text h='600' bold className='mb-4'>
            Role configuration
          </Text>
          <RestrictedAction feature='set_auth_methods'>
            {({ can, may }) => (
              <div className='my-1 flex flex-row items-center space-x-2'>
                <Toggle
                  name='role_for_fund_study'
                  on={permissions.fund_study === 'admin'}
                  onToggle={handleToggleFundStudy}
                  disabled={!can || !may}
                />
                <Text h='400' color={can && may ? 'gray-700' : 'gray-500'}>
                  Require Admin approval for funding of studies.
                </Text>
              </div>
            )}
          </RestrictedAction>
          <RestrictedAction feature='set_auth_methods'>
            {({ can, may }) => (
              <div className='my-1 flex flex-row items-center space-x-2'>
                <Toggle
                  name='role_for_approve_studies'
                  on={permissions.approve_studies === 'admin'}
                  onToggle={handleToggleApproveStudies}
                  disabled={!can || !may}
                />
                <Text h='400' color={can && may ? 'gray-700' : 'gray-500'}>
                  Require Admin approval for activating studies.
                </Text>
              </div>
            )}
          </RestrictedAction>
        </div>
        <Text h='600' bold mb='4'>
          Tag management
        </Text>
        <Text h='400' color='gray-500' mb='4'>
          Set which roles can add, edit or delete tags.
        </Text>
        <PermissionsMenu />
        <div className='mt-10'>{hasBetterFeatureRestrictions ? <BetterDisabledFeatures /> : <DisabledFeatures />}</div>
      </div>
    </>
  );
};
